'use client';
import {
  bindingRequiresFrameworkReinspection,
  bindingWebsiteFramework,
} from '@/lib/github/preview/framework-profile';

import {
  isGitHubWebsiteEnabledClient,
  isGitHubBindingEnabledClient,
} from '@/lib/github/feature-flags';

import { integrationSetupReturnPath } from '@/lib/integrations/setup-return';
import { useIntegrationSetupFlow } from './useIntegrationSetupFlow';
import { useConnectorDraft } from './useConnectorDraft';
import { IntegrationSetupError } from './IntegrationSetupError';

import {
  Badge,
  Box,
  Button,
  Field,
  HStack,
  Input,
  NativeSelect,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { GitBranch, Github, KeyRound, RefreshCw, ShieldCheck, Unlink } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/app/auth-provider';
import {
  IntegrationCard,
  IntegrationNotice,
  IntegrationStatusRow,
} from '@/components/shared/integrations/IntegrationCardShell';
import { useProject } from '@/contexts/ProjectContext';
import { isAttuneDesktopShell } from '@/lib/desktop-auth-client';

const DEFAULT_ASTRO_CONTENT_GLOBS = [
  'src/content/**/*.{md,mdx,mdoc,json,yaml,yml,toml}',
  'src/pages/**/*.{md,mdx,mdoc}',
];
const DEFAULT_ASTRO_EDITABLE_GLOBS = [
  'src/pages/**/*',
  'src/components/**/*',
  'src/layouts/**/*',
  'src/styles/**/*',
  'src/content/**/*',
  'public/**/*',
];
const DEFAULT_ASTRO_ASSET_GLOBS = ['src/assets/**/*', 'public/**/*'];

interface GitHubStatus {
  installation?: { status?: string; account_login?: string } | null;
  binding?: {
    framework?: 'astro' | 'next' | null;
    detected_astro?: boolean;
    github_repository_id: string;
    repository_full_name: string;
    repository_html_url: string;
    status: string;
    status_detail?: string | null;
    default_branch: string;
    merge_method: string;
    content_globs: string[];
    editable_globs: string[];
    asset_globs: string[];
    site_root: string;
    preview_port: number;
    url_templates: Record<string, string>;
    last_sync_at?: string | null;
  } | null;
  contentCount?: number;
  previewWebflowCredential?: {
    connected: true;
    removalPending?: boolean;
    rotationPending?: boolean;
    webflowSiteId: string;
    tokenEnvVar: string;
    siteIdEnvVar: string;
    stagedModeEnvVar: string | null;
    stagedModeEnvValue: string | null;
    credentialVersion: number;
    verifiedAt: string;
  } | null;
}

interface RepositoryOption {
  id: number;
  full_name: string;
  archived: boolean;
  disabled: boolean;
}

async function message(response: Response, fallback: string) {
  const payload = (await response.json().catch(() => ({}))) as { error?: string };
  return payload.error || fallback;
}

function customLoaderWarnings(detail: string | null | undefined): string[] {
  if (!detail?.includes('custom loader is external')) return [];
  return detail
    .split(/(?<=\.)\s+/)
    .map(value => value.trim())
    .filter(Boolean);
}

export function GitHubAstroIntegration({
  onStatusChanged,
  canManage = false,
}: {
  onStatusChanged?: () => void;
  canManage?: boolean;
}) {
  const { user } = useAuth();
  const { currentProject } = useProject();
  const setup = useIntegrationSetupFlow(currentProject?.id);
  const projectId = currentProject?.id || '';
  const [status, setStatus] = useState<GitHubStatus>({});
  const [statusProjectId, setStatusProjectId] = useState<string | null>(null);
  const websiteEnabled = isGitHubWebsiteEnabledClient();
  const statusReady = statusProjectId === projectId;
  const bindingDisabled = Boolean(
    bindingWebsiteFramework(status.binding || {}) && !isGitHubBindingEnabledClient(status.binding)
  );
  const [repositories, setRepositories] = useState<RepositoryOption[]>([]);
  const [repositoryId, setRepositoryId] = useState('');
  const [contentGlobs, setContentGlobs] = useState('');
  const [editableGlobs, setEditableGlobs] = useState(DEFAULT_ASTRO_EDITABLE_GLOBS.join('\n'));
  const [assetGlobs, setAssetGlobs] = useState(DEFAULT_ASTRO_ASSET_GLOBS.join('\n'));
  const [siteRoot, setSiteRoot] = useState('.');
  const [siteRootExplicit, setSiteRootExplicit] = useState(false);
  const [urlTemplate, setUrlTemplate] = useState('');
  const [showStructuredContent, setShowStructuredContent] = useState(false);
  const [layoutLoading, setLayoutLoading] = useState(false);
  const [inspectedLayoutKey, setInspectedLayoutKey] = useState<string | null>(null);
  const [layoutAttempt, setLayoutAttempt] = useState(0);
  const [inspectionRequestedKey, setInspectionRequestedKey] = useState<string | null>(null);
  const normalizedSiteRoot = siteRoot.trim() || '.';
  const layoutKey = JSON.stringify([projectId, repositoryId, normalizedSiteRoot]);
  const bindingMatchesSelection =
    status.binding &&
    String(status.binding.github_repository_id) === repositoryId &&
    (status.binding.site_root || '.') === normalizedSiteRoot;
  const requiresFrameworkInspection = Boolean(
    status.binding && bindingRequiresFrameworkReinspection(status.binding)
  );
  const layoutReady =
    (!siteRootExplicit && !requiresFrameworkInspection && Boolean(bindingMatchesSelection)) ||
    inspectedLayoutKey === layoutKey;
  const [mergeMethod, setMergeMethod] = useState<'squash' | 'merge' | 'rebase'>('squash');
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [previewCredentialWorking, setPreviewCredentialWorking] = useState(false);
  const [previewSiteId, setPreviewSiteId] = useState('');
  const [previewToken, setPreviewToken] = useState('');
  const [tokenEnvVar, setTokenEnvVar] = useState('WEBFLOW_API_TOKEN');
  const [siteIdEnvVar, setSiteIdEnvVar] = useState('WEBFLOW_SITE_ID');
  const [stagedModeEnvVar, setStagedModeEnvVar] = useState('');
  const [stagedModeEnvValue, setStagedModeEnvValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [checkingConnection, setCheckingConnection] = useState(false);
  const [connectionIssue, setConnectionIssue] = useState(false);
  const [connectionCheck, setConnectionCheck] = useState<string | null>(null);
  const loadRequestRef = useRef(0);
  const projectIdRef = useRef(projectId);
  projectIdRef.current = projectId;

  const draftRef = useRef<Record<string, string>>({});
  draftRef.current = {
    repositoryId,
    siteRoot,
    contentGlobs,
    editableGlobs,
    assetGlobs,
    urlTemplate,
  };
  const clearConnectionDraft = useConnectorDraft(
    user?.uid,
    projectId,
    'github',
    draftRef.current,
    draft => {
      draftRef.current = draft;
      setRepositoryId(draft.repositoryId || '');
      setSiteRoot(draft.siteRoot || '.');
      setContentGlobs(draft.contentGlobs || '');
      setEditableGlobs(draft.editableGlobs || DEFAULT_ASTRO_EDITABLE_GLOBS.join('\n'));
      setAssetGlobs(draft.assetGlobs || DEFAULT_ASTRO_ASSET_GLOBS.join('\n'));
      setUrlTemplate(draft.urlTemplate || '');
    }
  );

  const authorizedFetch = useCallback(
    async (url: string, init?: RequestInit) => {
      if (!user) throw new Error('Sign in to manage GitHub');
      const token = await user.getIdToken();
      return fetch(url, {
        ...init,
        cache: 'no-store',
        headers: {
          Authorization: `Bearer ${token}`,
          ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
          ...init?.headers,
        },
      });
    },
    [user]
  );

  const load = useCallback(async () => {
    if (!user || !projectId || !websiteEnabled) return;
    const requestId = ++loadRequestRef.current;
    const isCurrentLoad = () => loadRequestRef.current === requestId;
    setLoading(true);
    setError(null);
    try {
      const response = await authorizedFetch(
        `/api/integrations/github/status?projectId=${encodeURIComponent(projectId)}`
      );
      if (!response.ok) throw new Error(await message(response, 'Failed to load GitHub status'));
      const next = (await response.json()) as GitHubStatus;
      if (!isCurrentLoad()) return;
      setStatus(next);
      setStatusProjectId(projectId);
      setInspectedLayoutKey(null);
      const needsFrameworkInspection = Boolean(
        next.binding && bindingRequiresFrameworkReinspection(next.binding)
      );
      setSiteRootExplicit(needsFrameworkInspection);
      setInspectionRequestedKey(null);
      if (next.binding) {
        setRepositoryId(String(next.binding.github_repository_id));
        setContentGlobs(next.binding.content_globs.join('\n'));
        setEditableGlobs(next.binding.editable_globs.join('\n'));
        setAssetGlobs(next.binding.asset_globs.join('\n'));
        setSiteRoot(next.binding.site_root || '.');
        setMergeMethod(next.binding.merge_method as typeof mergeMethod);
        setUrlTemplate(next.binding.url_templates?.['*'] || '');
        setShowStructuredContent(
          next.binding.content_globs.length > 0 || Boolean(next.binding.url_templates?.['*'])
        );
      } else {
        setRepositoryId(draftRef.current.repositoryId || '');
        setContentGlobs(draftRef.current.contentGlobs || '');
        setEditableGlobs(draftRef.current.editableGlobs || DEFAULT_ASTRO_EDITABLE_GLOBS.join('\n'));
        setAssetGlobs(draftRef.current.assetGlobs || DEFAULT_ASTRO_ASSET_GLOBS.join('\n'));
        setSiteRoot(draftRef.current.siteRoot || '.');
        setMergeMethod('squash');
        setUrlTemplate(draftRef.current.urlTemplate || '');
        setShowStructuredContent(false);
      }
      if (next.previewWebflowCredential) {
        setPreviewSiteId(next.previewWebflowCredential.webflowSiteId);
        setTokenEnvVar(next.previewWebflowCredential.tokenEnvVar);
        setSiteIdEnvVar(next.previewWebflowCredential.siteIdEnvVar);
        setStagedModeEnvVar(next.previewWebflowCredential.stagedModeEnvVar || '');
        setStagedModeEnvValue(next.previewWebflowCredential.stagedModeEnvValue || '');
      } else {
        setPreviewSiteId('');
        setPreviewToken('');
        setTokenEnvVar('WEBFLOW_API_TOKEN');
        setSiteIdEnvVar('WEBFLOW_SITE_ID');
        setStagedModeEnvVar('');
        setStagedModeEnvValue('');
      }
      if (next.installation && !next.binding) {
        const repositoriesResponse = await authorizedFetch(
          `/api/integrations/github/repositories?projectId=${encodeURIComponent(projectId)}`
        );
        if (!repositoriesResponse.ok) {
          throw new Error(await message(repositoriesResponse, 'Failed to list repositories'));
        }
        const payload = (await repositoriesResponse.json()) as {
          repositories?: RepositoryOption[];
        };
        if (!isCurrentLoad()) return;
        const options = (payload.repositories || []).filter(
          repo => !repo.archived && !repo.disabled
        );
        setRepositories(options);
        setRepositoryId(current =>
          options.some(repo => String(repo.id) === current)
            ? current
            : options.length === 1
              ? String(options[0].id)
              : ''
        );
      } else {
        setRepositories([]);
      }
    } catch (cause) {
      if (isCurrentLoad()) {
        setError(cause instanceof Error ? cause.message : 'Failed to load GitHub status');
      }
    } finally {
      if (isCurrentLoad()) setLoading(false);
    }
  }, [authorizedFetch, projectId, user, websiteEnabled]);

  useEffect(() => {
    loadRequestRef.current += 1;
    setStatus({});
    setConnectionCheck(null);
    setConnectionIssue(false);
    setCheckingConnection(false);
    setStatusProjectId(null);
    setRepositories([]);
    setShowStructuredContent(false);
    setLayoutLoading(false);
    setInspectedLayoutKey(null);
    setSiteRootExplicit(false);
    setMergeMethod('squash');
    setPreviewSiteId('');
    setPreviewToken('');
    setTokenEnvVar('WEBFLOW_API_TOKEN');
    setSiteIdEnvVar('WEBFLOW_SITE_ID');
    setStagedModeEnvVar('');
    setStagedModeEnvValue('');
    setError(null);
    setLoading(Boolean(projectId));
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (
      !websiteEnabled ||
      bindingDisabled ||
      !statusReady ||
      loading ||
      !repositoryId ||
      !projectId ||
      !user ||
      layoutReady ||
      (siteRootExplicit && inspectionRequestedKey !== layoutKey)
    )
      return;
    let cancelled = false;
    const controller = new AbortController();
    setInspectedLayoutKey(null);
    setLayoutLoading(true);
    void authorizedFetch(
      `/api/integrations/github/repositories?projectId=${encodeURIComponent(projectId)}&repositoryId=${encodeURIComponent(repositoryId)}${siteRootExplicit ? `&siteRoot=${encodeURIComponent(normalizedSiteRoot)}` : ''}`,
      { signal: controller.signal }
    )
      .then(async response => {
        if (!response.ok) throw new Error(await message(response, 'Failed to inspect repository'));
        return response.json() as Promise<{
          layout?: {
            framework: 'astro' | 'next';
            siteRoot: string;
            editableGlobs: string[];
            assetGlobs: string[];
          };
        }>;
      })
      .then(payload => {
        if (cancelled) return;
        if (!payload.layout) throw new Error('Repository inspection did not return editing roots');
        if (!isGitHubBindingEnabledClient(payload.layout))
          throw new Error('Website editing for this framework is currently unavailable.');
        setSiteRoot(payload.layout.siteRoot);
        setEditableGlobs(payload.layout.editableGlobs.join('\n'));
        setAssetGlobs(payload.layout.assetGlobs.join('\n'));
        if (status.binding) {
          const sameSite =
            !requiresFrameworkInspection &&
            String(status.binding.github_repository_id) === repositoryId &&
            (status.binding.site_root || '.') === payload.layout.siteRoot;
          setContentGlobs(sameSite ? status.binding.content_globs.join('\n') : '');
          setUrlTemplate(sameSite ? status.binding.url_templates?.['*'] || '' : '');
          setShowStructuredContent(
            sameSite &&
              (status.binding.content_globs.length > 0 ||
                Boolean(status.binding.url_templates?.['*']))
          );
        }
        setInspectedLayoutKey(JSON.stringify([projectId, repositoryId, payload.layout.siteRoot]));
      })
      .catch(cause => {
        if (!cancelled)
          setup.reportError(cause instanceof Error ? cause.message : 'Inspection failed');
      })
      .finally(() => {
        if (!cancelled) setLayoutLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    authorizedFetch,
    websiteEnabled,
    bindingDisabled,
    statusReady,
    loading,
    inspectionRequestedKey,
    requiresFrameworkInspection,
    layoutAttempt,
    layoutKey,
    layoutReady,
    normalizedSiteRoot,
    projectId,
    repositoryId,
    siteRootExplicit,
    status.binding,
    user,
  ]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('github_success')) toast.success('GitHub App installed. Select a repository.');
    if (params.get('github_error'))
      setup.reportError('GitHub connection failed. Review App permissions.');
    if (!params.get('github_success') && !params.get('github_error')) return;
    params.delete('github_success');
    params.delete('github_error');
    params.delete('repositories');
    const search = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${search ? `?${search}` : ''}`);
  }, []);

  const checkConnection = async () => {
    if (!canManage || checkingConnection || working || loading) return;
    const requestId = ++loadRequestRef.current;
    const isCurrent = () => loadRequestRef.current === requestId;
    setCheckingConnection(true);
    setConnectionCheck(null);
    setError(null);
    try {
      const response = await authorizedFetch(
        `/api/integrations/github/status?projectId=${encodeURIComponent(projectId)}`
      );
      if (!response.ok) throw new Error(await message(response, 'Failed to check GitHub status'));
      const next = (await response.json()) as GitHubStatus;
      if (!isCurrent()) return;
      if (!next.installation) {
        setStatus(next);
        setRepositories([]);
        setRepositoryId('');
        setInspectedLayoutKey(null);
        setConnectionIssue(true);
        setConnectionCheck(
          'No GitHub connection found for this workspace. Complete Install GitHub App to authorize access, then check again.'
        );
        return;
      }
      const repositoriesResponse = await authorizedFetch(
        `/api/integrations/github/repositories?projectId=${encodeURIComponent(projectId)}&checkConnection=true`
      );
      if (!repositoriesResponse.ok)
        throw new Error(await message(repositoriesResponse, 'Failed to check repository access'));
      const payload = (await repositoriesResponse.json()) as { repositories?: RepositoryOption[] };
      if (!isCurrent()) return;
      const inventory = payload.repositories || [];
      const boundRepository =
        next.binding &&
        inventory.find(repo => String(repo.id) === String(next.binding?.github_repository_id));
      if (next.binding && !boundRepository) {
        throw new Error(
          'GitHub no longer grants access to the connected repository. Review repository access in GitHub.'
        );
      }
      const unavailable = Boolean(boundRepository?.archived || boundRepository?.disabled);
      setConnectionIssue(unavailable);
      setConnectionCheck(
        boundRepository?.archived
          ? 'Repository access verified, but this repository is archived and read-only.'
          : boundRepository?.disabled
            ? 'Repository access verified, but GitHub has disabled this repository.'
            : next.binding
              ? 'GitHub connection verified.'
              : inventory.some(repo => !repo.archived && !repo.disabled)
                ? 'Repository access refreshed. Select a repository below.'
                : 'GitHub is connected, but no available repositories were found. Add repository access in GitHub, then check again.'
      );
      // Refresh status and choices only; never reset the user's unsaved form fields.
      setStatus(next);
      const options = inventory.filter(repo => !repo.archived && !repo.disabled);
      setRepositories(options);
      if (
        !next.binding &&
        !options.some(repo => String(repo.id) === draftRef.current.repositoryId)
      ) {
        setRepositoryId('');
        setInspectedLayoutKey(null);
        setInspectionRequestedKey(null);
      }
    } catch (cause) {
      if (isCurrent()) {
        setConnectionIssue(true);
        setError(cause instanceof Error ? cause.message : 'Failed to check GitHub connection');
      }
    } finally {
      if (projectIdRef.current === projectId) setCheckingConnection(false);
    }
  };

  const connect = async () => {
    if (!canManage || working || checkingConnection || loading || !statusReady) return;
    setWorking(true);
    try {
      const response = await authorizedFetch('/api/integrations/github/start', {
        method: 'POST',
        body: JSON.stringify({
          projectId,
          returnTo: integrationSetupReturnPath('github', setup.returnTo),
          desktopHandoff: isAttuneDesktopShell(),
        }),
      });
      if (!response.ok) throw new Error(await message(response, 'Failed to start GitHub install'));
      const payload = (await response.json()) as { installationUrl: string };
      window.location.assign(payload.installationUrl);
    } catch (cause) {
      setup.reportError(cause instanceof Error ? cause.message : 'Failed to start GitHub install');
      setWorking(false);
    }
  };

  const bind = async () => {
    if (
      !websiteEnabled ||
      bindingDisabled ||
      !statusReady ||
      loading ||
      !layoutReady ||
      layoutLoading
    )
      return;
    const initiatingProjectId = projectId;
    setWorking(true);
    try {
      const response = await authorizedFetch('/api/integrations/github/bind', {
        method: 'POST',
        body: JSON.stringify({
          projectId,
          repositoryId,
          contentGlobs: contentGlobs
            .split(/[\n,]/)
            .map(value => value.trim())
            .filter(Boolean),
          editableGlobs: editableGlobs
            .split(/[\n,]/)
            .map(value => value.trim())
            .filter(Boolean),
          assetGlobs: assetGlobs
            .split(/[\n,]/)
            .map(value => value.trim())
            .filter(Boolean),
          siteRoot: siteRoot.trim() || '.',
          previewPort:
            !requiresFrameworkInspection &&
            status.binding?.github_repository_id === repositoryId &&
            status.binding.site_root === siteRoot.trim()
              ? status.binding.preview_port
              : undefined,
          urlTemplates:
            (bindingMatchesSelection && !requiresFrameworkInspection
              ? status.binding?.url_templates
              : null) || (urlTemplate.trim() ? { '*': urlTemplate.trim() } : {}),
          mergeMethod,
        }),
      });
      if (!response.ok) throw new Error(await message(response, 'Failed to bind repository'));
      if (projectIdRef.current !== initiatingProjectId) return;
      toast.success(
        status.binding ? 'Website editing roots updated.' : 'Website repository connected.'
      );
      setConnectionIssue(false);
      setConnectionCheck(null);
      await load();
      if (projectIdRef.current === initiatingProjectId) {
        onStatusChanged?.();
        clearConnectionDraft();
        setup.onConfigured();
      }
    } catch (cause) {
      setup.reportError(cause instanceof Error ? cause.message : 'Failed to bind repository');
    } finally {
      setWorking(false);
    }
  };

  const mutate = async (action: 'sync' | 'disconnect') => {
    if (!statusReady || loading || (action === 'sync' && (!websiteEnabled || bindingDisabled)))
      return;
    const initiatingProjectId = projectId;
    setWorking(true);
    try {
      const response = await authorizedFetch(`/api/integrations/github/${action}`, {
        method: 'POST',
        body: JSON.stringify({ projectId }),
      });
      if (!response.ok) throw new Error(await message(response, `Failed to ${action} GitHub`));
      if (projectIdRef.current !== initiatingProjectId) return;
      setConnectionIssue(false);
      setConnectionCheck(null);
      toast.success(action === 'sync' ? 'GitHub content index refreshed.' : 'GitHub disconnected.');
      await load();
      onStatusChanged?.();
    } catch (cause) {
      setup.reportError(cause instanceof Error ? cause.message : `Failed to ${action} GitHub`);
    } finally {
      setWorking(false);
    }
  };

  const savePreviewCredential = async () => {
    setPreviewCredentialWorking(true);
    try {
      const response = await authorizedFetch('/api/integrations/github/webflow-preview', {
        method: 'PUT',
        body: JSON.stringify({
          projectId,
          siteId: previewSiteId,
          token: previewToken,
          tokenEnvVar,
          siteIdEnvVar,
          stagedModeEnvVar: stagedModeEnvVar || null,
          stagedModeEnvValue: stagedModeEnvVar ? stagedModeEnvValue : null,
        }),
      });
      const payload = (await response.json()) as {
        error?: string;
        code?: string;
        previewRefresh?: { failed?: number };
      };
      if (!response.ok) {
        if (
          response.status === 409 &&
          payload.code === 'GITHUB_PREVIEW_CREDENTIAL_ROTATION_PENDING'
        ) {
          setStatus(current => ({
            ...current,
            previewWebflowCredential: current.previewWebflowCredential
              ? { ...current.previewWebflowCredential, rotationPending: true }
              : null,
          }));
          await load();
          toast.warning('Token saved; existing preview cleanup is still finishing. Retry shortly.');
          return;
        }
        throw new Error(payload.error || 'Failed to connect Webflow preview token');
      }
      setPreviewToken('');
      if (payload.previewRefresh?.failed) {
        toast.warning('Token saved, but an existing preview needs to be restarted.');
      } else {
        toast.success('Read-only Webflow preview token connected.');
      }
      await load();
    } catch (cause) {
      setup.reportError(cause instanceof Error ? cause.message : 'Failed to connect preview token');
    } finally {
      setPreviewCredentialWorking(false);
    }
  };

  const removePreviewCredential = async () => {
    if (!window.confirm('Remove the stored Webflow preview token?')) return;
    setPreviewCredentialWorking(true);
    try {
      const response = await authorizedFetch('/api/integrations/github/webflow-preview', {
        method: 'DELETE',
        body: JSON.stringify({ projectId }),
      });
      const payload = (await response.json().catch(() => ({}))) as {
        error?: string;
        code?: string;
      };
      if (!response.ok) {
        if (
          response.status === 409 &&
          payload.code === 'GITHUB_PREVIEW_CREDENTIAL_REMOVAL_PENDING'
        ) {
          setStatus(current => ({
            ...current,
            previewWebflowCredential: current.previewWebflowCredential
              ? { ...current.previewWebflowCredential, removalPending: true }
              : null,
          }));
          await load();
          toast.warning('Token access is disabled; preview cleanup is still finishing.');
          return;
        }
        throw new Error(payload.error || 'Failed to remove preview token');
      }
      setStatus(current => ({ ...current, previewWebflowCredential: null }));
      setPreviewToken('');
      toast.success('Webflow preview token removed.');
      await load();
    } catch (cause) {
      setup.reportError(cause instanceof Error ? cause.message : 'Failed to remove preview token');
    } finally {
      setPreviewCredentialWorking(false);
    }
  };

  if (!websiteEnabled || (statusReady && bindingDisabled)) {
    return (
      <IntegrationCard
        icon={<Github size={18} />}
        title="Website via GitHub"
        description="Manage the connected website repository."
        statusLabel="Unavailable"
        statusTone="warning"
      >
        <IntegrationNotice tone="neutral">
          Website editing for this repository is currently unavailable.
        </IntegrationNotice>
        {statusReady && status.binding ? (
          <HStack pt="3" gap="2">
            {status.previewWebflowCredential ? (
              <Button
                size="sm"
                variant="ghost"
                colorPalette="red"
                onClick={() => void removePreviewCredential()}
                disabled={previewCredentialWorking}
              >
                Remove
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="ghost"
              colorPalette="red"
              onClick={() => void mutate('disconnect')}
              disabled={working}
            >
              Disconnect
            </Button>
          </HStack>
        ) : null}
      </IntegrationCard>
    );
  }
  if (!statusReady || loading) {
    return (
      <IntegrationCard
        icon={<Github size={18} />}
        title="Website via GitHub"
        description="Manage the connected website repository."
        statusLabel={error ? 'Status unavailable' : 'Checking'}
        statusTone={error ? 'danger' : 'idle'}
      >
        {error ? (
          <>
            <IntegrationNotice tone="danger">{error}</IntegrationNotice>
            <Button size="sm" onClick={() => void load()}>
              Retry
            </Button>
          </>
        ) : (
          <Text>Loading repository settings…</Text>
        )}
      </IntegrationCard>
    );
  }

  const connected = Boolean(status.binding && isGitHubBindingEnabledClient(status.binding));
  const readOnly = status.binding?.status === 'read_only' || connectionIssue;
  const loaderWarnings = customLoaderWarnings(status.binding?.status_detail);
  const needsAttention = Boolean(
    connected &&
      (connectionIssue ||
        status.binding?.status !== 'active' ||
        (status.installation?.status && status.installation.status !== 'connected'))
  );
  return (
    <IntegrationCard
      icon={<Github size={18} />}
      title="Website via GitHub"
      description="Edit Astro or Next.js pages, metadata, structured data, styles and images in a private preview, then review and submit an approved pull request."
      statusLabel={
        loading
          ? 'Checking'
          : connectionIssue
            ? 'Needs attention'
            : connected
              ? readOnly
                ? 'Read only'
                : needsAttention
                  ? 'Needs attention'
                  : 'Connected'
              : 'Not connected'
      }
      statusTone={
        connectionIssue
          ? 'warning'
          : connected
            ? needsAttention
              ? 'warning'
              : 'connected'
            : error
              ? 'danger'
              : 'idle'
      }
      action={
        <HStack gap="2" flexWrap="wrap">
          {!connected && canManage ? (
            <Button
              size="sm"
              onClick={() => void connect()}
              loading={working}
              disabled={loading || checkingConnection || !statusReady}
            >
              {status.installation ? 'Add repository access' : 'Install GitHub App'}
            </Button>
          ) : null}
          {canManage && (connected || !status.installation) ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => void checkConnection()}
              disabled={working || loading}
              loading={checkingConnection}
            >
              <RefreshCw size={15} /> Check connection
            </Button>
          ) : null}
        </HStack>
      }
    >
      <IntegrationSetupError message={setup.error} />
      {connectionCheck ? (
        <IntegrationNotice tone="neutral">{connectionCheck}</IntegrationNotice>
      ) : null}
      {error ? <IntegrationNotice tone="danger">{error}</IntegrationNotice> : null}
      {status.installation && !status.binding ? (
        <VStack align="stretch" gap="4" py="3">
          <IntegrationNotice tone="neutral">
            Missing a repository? Use Add repository access to authorize it in GitHub, then return
            and click Refresh repositories. Repository admin access alone does not grant Attune
            access.
          </IntegrationNotice>
          <Field.Root required>
            <HStack justify="space-between" flexWrap="wrap" w="full">
              <Field.Label>Repository</Field.Label>
              {canManage ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void checkConnection()}
                  disabled={working || loading}
                  loading={checkingConnection}
                >
                  <RefreshCw size={15} /> Refresh repositories
                </Button>
              ) : null}
            </HStack>
            <NativeSelect.Root disabled={working || loading || checkingConnection}>
              <NativeSelect.Field
                value={repositoryId}
                onChange={event => {
                  setRepositoryId(event.target.value);
                  setSiteRootExplicit(false);
                }}
              >
                <option value="">Select repository</option>
                {repositories.map(repo => (
                  <option key={repo.id} value={repo.id}>
                    {repo.full_name}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
            {status.installation.account_login ? (
              <Field.HelperText>
                Repositories authorized for {status.installation.account_login}.
              </Field.HelperText>
            ) : null}
          </Field.Root>
          <Field.Root required>
            <Field.Label>Approved code roots</Field.Label>
            <Textarea
              value={editableGlobs}
              disabled={!layoutReady}
              onChange={event => setEditableGlobs(event.target.value)}
              rows={6}
            />
            <Field.HelperText>
              Covers pages, components, layouts, styles, and text content. Dependencies, config,
              lockfiles, secrets, workflows, symlinks, and build output remain blocked.
            </Field.HelperText>
          </Field.Root>
          <Field.Root required>
            <Field.Label>Approved media roots</Field.Label>
            <Textarea
              value={assetGlobs}
              disabled={!layoutReady}
              onChange={event => setAssetGlobs(event.target.value)}
              rows={4}
            />
            <Field.HelperText>
              AI may create, replace, or delete PNG, JPEG, WebP, GIF, and AVIF files only here.
              Uploaded SVG remains blocked. Confirm these roots explicitly.
            </Field.HelperText>
          </Field.Root>
          <Field.Root required>
            <Field.Label>Website site root</Field.Label>
            <Input
              value={siteRoot}
              aria-label="Website site root"
              onChange={event => {
                setSiteRoot(event.target.value);
                setSiteRootExplicit(true);
                setInspectionRequestedKey(null);
                setLayoutLoading(false);
              }}
            />
            <Field.HelperText>
              {layoutLoading
                ? 'Inspecting this repository…'
                : 'Detected automatically. Use . for a root project or a repository-relative monorepo folder.'}
            </Field.HelperText>
          </Field.Root>
          <Field.Root required>
            <Field.Label>Merge method</Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                value={mergeMethod}
                onChange={event => setMergeMethod(event.target.value as typeof mergeMethod)}
              >
                <option value="squash">Squash</option>
                <option value="merge">Merge commit</option>
                <option value="rebase">Rebase</option>
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>
          <Box borderWidth="1px" borderColor="ui.border.subtle" borderRadius="md" p="3">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                const next = !showStructuredContent;
                setShowStructuredContent(next);
                if (next && !contentGlobs.trim()) {
                  setContentGlobs(DEFAULT_ASTRO_CONTENT_GLOBS.join('\n'));
                }
              }}
            >
              {showStructuredContent ? 'Hide' : 'Configure'} Git-backed structured content
            </Button>
            {showStructuredContent ? (
              <VStack align="stretch" gap="3" mt="3">
                <Field.Root>
                  <Field.Label>Content globs</Field.Label>
                  <Textarea
                    value={contentGlobs}
                    onChange={event => setContentGlobs(event.target.value)}
                    rows={4}
                  />
                  <Field.HelperText>
                    Optional indexing for Markdown, MDX, Markdoc, JSON, YAML, or TOML content.
                  </Field.HelperText>
                </Field.Root>
                <Field.Root>
                  <Field.Label>Canonical URL template</Field.Label>
                  <Input
                    value={urlTemplate}
                    onChange={event => setUrlTemplate(event.target.value)}
                    placeholder="https://example.com/blog/{slug}"
                  />
                  <Field.HelperText>
                    Optional; use {'{slug}'} or {'{path}'}.
                  </Field.HelperText>
                </Field.Root>
              </VStack>
            ) : null}
          </Box>
          {repositoryId && !layoutLoading && !layoutReady ? (
            <Button
              variant="outline"
              onClick={() => {
                setInspectionRequestedKey(layoutKey);
                setLayoutAttempt(attempt => attempt + 1);
              }}
            >
              {siteRootExplicit && inspectionRequestedKey !== layoutKey
                ? 'Inspect site root'
                : 'Retry inspection'}
            </Button>
          ) : null}
          <Button
            onClick={() => void bind()}
            loading={working}
            disabled={
              !layoutReady ||
              !repositoryId ||
              !siteRoot.trim() ||
              !editableGlobs.trim() ||
              !assetGlobs.trim() ||
              layoutLoading
            }
          >
            Connect repository
          </Button>
        </VStack>
      ) : null}
      {status.binding ? (
        <VStack align="stretch" gap="0">
          <IntegrationStatusRow
            icon={<GitBranch size={16} />}
            title={status.binding.repository_full_name}
            description={`${status.binding.default_branch} · ${status.contentCount || 0} indexed files · ${status.binding.merge_method} merge`}
            action={
              <Badge colorPalette={readOnly ? 'orange' : 'green'}>
                {connectionIssue ? 'Needs attention' : status.binding.status}
              </Badge>
            }
          />
          <IntegrationStatusRow
            icon={<ShieldCheck size={16} />}
            title="Manager-governed delivery"
            description="AI co-edits only approved UI roots in a private live preview. The exact successful build revision is frozen for approval; only Approvals can create or merge its PR."
          />
          {!readOnly ? (
            <Box
              mt="3"
              p="4"
              borderWidth="1px"
              borderColor="ui.border.subtle"
              borderRadius="glass"
              bg="ui.surface.selected"
            >
              <VStack align="stretch" gap="3">
                <Box>
                  <Text fontSize="sm" fontWeight="700">
                    Approved website-editing roots
                  </Text>
                  <Text mt="1" fontSize="xs" color="ui.text.tertiary">
                    Managers can update these roots in place. Changing them removes existing private
                    previews before the new capability takes effect.
                  </Text>
                </Box>
                <Field.Root required>
                  <Field.Label>Approved code roots</Field.Label>
                  <Textarea
                    value={editableGlobs}
                    onChange={event => setEditableGlobs(event.target.value)}
                    rows={5}
                  />
                </Field.Root>
                <Field.Root required>
                  <Field.Label>Approved media roots</Field.Label>
                  <Textarea
                    value={assetGlobs}
                    onChange={event => setAssetGlobs(event.target.value)}
                    rows={4}
                    placeholder="public/assets/**/*"
                  />
                  <Field.HelperText>
                    Existing connections have no image access until a manager explicitly saves at
                    least one PNG, JPEG, WebP, GIF, or AVIF root here.
                  </Field.HelperText>
                </Field.Root>
                <HStack align="start" gap="3" flexWrap={{ base: 'wrap', md: 'nowrap' }}>
                  <Field.Root required>
                    <Field.Label>Website site root</Field.Label>
                    <Input
                      value={siteRoot}
                      aria-label="Website site root"
                      onChange={event => {
                        setSiteRoot(event.target.value);
                        setSiteRootExplicit(true);
                        setInspectionRequestedKey(null);
                        setLayoutLoading(false);
                      }}
                    />
                  </Field.Root>
                  <Field.Root required>
                    <Field.Label>Merge method</Field.Label>
                    <NativeSelect.Root>
                      <NativeSelect.Field
                        value={mergeMethod}
                        onChange={event => setMergeMethod(event.target.value as typeof mergeMethod)}
                      >
                        <option value="squash">Squash</option>
                        <option value="merge">Merge commit</option>
                        <option value="rebase">Rebase</option>
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>
                </HStack>
                {!layoutReady && !layoutLoading ? (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setInspectionRequestedKey(layoutKey);
                      setLayoutAttempt(attempt => attempt + 1);
                    }}
                  >
                    {siteRootExplicit && inspectionRequestedKey !== layoutKey
                      ? 'Inspect site root'
                      : 'Retry inspection'}
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  alignSelf="start"
                  onClick={() => void bind()}
                  loading={working}
                  disabled={
                    !layoutReady ||
                    layoutLoading ||
                    !repositoryId ||
                    !siteRoot.trim() ||
                    !editableGlobs.trim()
                  }
                >
                  Save editing roots
                </Button>
              </VStack>
            </Box>
          ) : null}
          {status.binding?.framework === 'next' && status.previewWebflowCredential ? (
            <Box mt="3" p="4" borderWidth="1px" borderColor="ui.border.subtle" borderRadius="glass">
              <Text fontSize="sm" fontWeight="700">
                Stored Webflow preview token
              </Text>
              <Text mt="1" fontSize="xs" color="ui.text.tertiary">
                This token is not used by the Next.js preview. You can remove the saved credential
                here.
              </Text>
              {status.previewWebflowCredential.removalPending ? (
                <Text fontSize="xs">
                  Removal is pending preview cleanup. Retry removal to finish.
                </Text>
              ) : null}
              <Button
                mt="2"
                size="sm"
                variant="ghost"
                colorPalette="red"
                onClick={() => void removePreviewCredential()}
                disabled={previewCredentialWorking}
              >
                Remove
              </Button>
            </Box>
          ) : null}
          {!readOnly && status.binding?.framework !== 'next' ? (
            <Box
              mt="3"
              p="4"
              borderWidth="1px"
              borderColor="ui.border.subtle"
              borderRadius="glass"
              bg="ui.surface.selected"
            >
              <VStack align="stretch" gap="3">
                <HStack justify="space-between" align="start" gap="3">
                  <Box>
                    <HStack gap="2">
                      <KeyRound size={15} />
                      <Text fontSize="sm" fontWeight="700">
                        Staged Webflow CMS preview
                      </Text>
                    </HStack>
                    <Text mt="1" fontSize="xs" color="ui.text.tertiary">
                      Use a separate site-specific token with CMS read scope only. It is injected
                      only into server-side Astro dev and build commands, never into browser-public
                      variables.
                    </Text>
                  </Box>
                  <Badge
                    colorPalette={
                      status.previewWebflowCredential?.removalPending
                        ? 'orange'
                        : status.previewWebflowCredential?.rotationPending
                          ? 'orange'
                          : status.previewWebflowCredential
                            ? 'green'
                            : 'gray'
                    }
                  >
                    {status.previewWebflowCredential?.removalPending
                      ? 'Removal pending'
                      : status.previewWebflowCredential?.rotationPending
                        ? 'Update pending'
                        : status.previewWebflowCredential
                          ? 'Connected'
                          : 'Optional'}
                  </Badge>
                </HStack>
                {status.previewWebflowCredential?.removalPending ? (
                  <Text fontSize="xs" color="orange.600">
                    Token access is disabled. Retry Remove to finish cleaning up the old preview.
                  </Text>
                ) : null}
                {status.previewWebflowCredential?.rotationPending ? (
                  <Text fontSize="xs" color="orange.600">
                    The new token is saved. Retry the update to finish replacing it in existing
                    previews.
                  </Text>
                ) : null}
                <Field.Root required>
                  <Field.Label>Webflow site ID</Field.Label>
                  <Input
                    value={previewSiteId}
                    onChange={event => setPreviewSiteId(event.target.value)}
                  />
                </Field.Root>
                <Field.Root required>
                  <Field.Label>CMS preview reader token</Field.Label>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    value={previewToken}
                    onChange={event => setPreviewToken(event.target.value)}
                    placeholder={
                      status.previewWebflowCredential ? 'Enter a new token to rotate' : ''
                    }
                  />
                  <Field.HelperText>
                    Attune verifies that the token can read this site&apos;s collections. Webflow
                    Site Tokens cannot be scope-introspected, so select only CMS read when creating
                    it.
                  </Field.HelperText>
                </Field.Root>
                <HStack align="start" gap="3" flexWrap={{ base: 'wrap', md: 'nowrap' }}>
                  <Field.Root required>
                    <Field.Label>Token variable</Field.Label>
                    <Input
                      value={tokenEnvVar}
                      onChange={event => setTokenEnvVar(event.target.value)}
                    />
                  </Field.Root>
                  <Field.Root required>
                    <Field.Label>Site ID variable</Field.Label>
                    <Input
                      value={siteIdEnvVar}
                      onChange={event => setSiteIdEnvVar(event.target.value)}
                    />
                  </Field.Root>
                </HStack>
                <HStack align="start" gap="3" flexWrap={{ base: 'wrap', md: 'nowrap' }}>
                  <Field.Root>
                    <Field.Label>Staged-mode variable (optional)</Field.Label>
                    <Input
                      value={stagedModeEnvVar}
                      onChange={event => setStagedModeEnvVar(event.target.value)}
                      placeholder="WEBFLOW_USE_STAGED"
                    />
                  </Field.Root>
                  <Field.Root disabled={!stagedModeEnvVar.trim()}>
                    <Field.Label>Staged-mode value</Field.Label>
                    <Input
                      value={stagedModeEnvValue}
                      onChange={event => setStagedModeEnvValue(event.target.value)}
                      placeholder="true"
                    />
                  </Field.Root>
                </HStack>
                <Text fontSize="xs" color="ui.text.tertiary">
                  These names must match the server-only variables your Astro CMS loader reads. That
                  loader must use Webflow&apos;s staged Items API for draft previews.
                </Text>
                <HStack gap="2" flexWrap="wrap">
                  <Button
                    size="sm"
                    onClick={() => void savePreviewCredential()}
                    loading={previewCredentialWorking}
                    disabled={!previewSiteId.trim() || !previewToken.trim()}
                  >
                    {status.previewWebflowCredential?.removalPending
                      ? 'Reconnect token'
                      : status.previewWebflowCredential?.rotationPending
                        ? 'Retry preview update'
                        : status.previewWebflowCredential
                          ? 'Rotate token'
                          : 'Connect preview token'}
                  </Button>
                  {status.previewWebflowCredential ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      colorPalette="red"
                      onClick={() => void removePreviewCredential()}
                      disabled={previewCredentialWorking}
                    >
                      Remove
                    </Button>
                  ) : null}
                </HStack>
              </VStack>
            </Box>
          ) : null}
          {loaderWarnings.length > 0 ? (
            <IntegrationNotice tone={readOnly ? 'warning' : 'neutral'}>
              <Text>
                {loaderWarnings.length} external CMS collections are not indexed from GitHub.
              </Text>
              <Box as="details" mt="2">
                <Box as="summary" cursor="pointer" fontWeight="600">
                  Review collections
                </Box>
                <VStack align="stretch" gap="1" mt="2">
                  {loaderWarnings.map((warning, index) => (
                    <Text key={`${index}-${warning}`} fontSize="xs">
                      {warning}
                    </Text>
                  ))}
                </VStack>
              </Box>
            </IntegrationNotice>
          ) : status.binding.status_detail ? (
            <IntegrationNotice tone={readOnly ? 'warning' : 'neutral'}>
              {status.binding.status_detail}
            </IntegrationNotice>
          ) : null}
          <HStack pt="3" gap="2" flexWrap="wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={() => void mutate('sync')}
              disabled={connectionIssue || checkingConnection}
              loading={working}
            >
              <RefreshCw size={15} /> Sync
            </Button>
            <Button
              size="sm"
              variant="ghost"
              colorPalette="red"
              onClick={() => void mutate('disconnect')}
              disabled={working}
            >
              <Unlink size={15} /> Disconnect
            </Button>
          </HStack>
          <Box pt="3">
            <Text fontSize="xs" color="ui.text.tertiary">
              Do not add Attune to branch or ruleset bypass lists. Merge queues remain read-only in
              v1.
            </Text>
          </Box>
        </VStack>
      ) : null}
    </IntegrationCard>
  );
}
